import React, { useState, useEffect, useRef, useCallback } from 'react'
import styles from './styles.module.css'
import OptimizedImage from '../../../../OptimizedImage'
import ZoomableImage from '../../ProductCheckoutPage/ProductImages/ZoomableImage'

// Product gallery: one large main image with a scrollable thumbnail
// strip and left/right arrows (main + thumbnail strip layout). Replaces the shared
// ProductImages photo-mosaic for products with several photos, keeping the buy
// box compact and freeing the vertical space the mosaic's stacked tiles eat.
// Started life on the GT-2 configurator, hence `resetKey` (it jumps back to the
// first photo when a colour/seat change swaps the image set); callers without
// that concern can omit it. Pure client
// component (selection state only); images are the same resolved descriptors the
// mosaic received, so it drops in with no data change.
// `aspectRatio` (CSS value, e.g. '2 / 1') shapes the main frame. It defaults to
// the square the GT-2 renders were cut for; a caller whose photos are a
// different shape passes their ratio, otherwise the image letterboxes inside
// the square and the empty band shoves the rest of the page down.
function ProductGallery({ images, resetKey, aspectRatio }) {
  const [active, setActive] = useState(0)
  const stripRef = useRef(null)
  const thumbRefs = useRef([])

  const count = images ? images.length : 0

  // Reset to the first image whenever the underlying set changes (e.g. colour or
  // seat toggle swaps the image array).
  useEffect(() => { setActive(0) }, [resetKey, count])

  // Keep the active thumbnail scrolled into view in the strip.
  useEffect(() => {
    const el = thumbRefs.current[active]
    if (el && el.scrollIntoView) el.scrollIntoView({ block: 'nearest', inline: 'center', behavior: 'smooth' })
  }, [active])

  const go = useCallback((dir) => {
    setActive((i) => (count === 0 ? 0 : (i + dir + count) % count))
  }, [count])

  if (!images || count === 0) return null

  const main = images[active] || images[0]

  return (
    <div className={styles.gallery}>
      <div className={styles.mainWrap}>
        {count > 1 && (
          <button type="button" className={[styles.arrow, styles.arrowLeft].join(' ')} onClick={() => go(-1)} aria-label="Previous image">
            <Chevron dir="left" />
          </button>
        )}
        {/* Zoom reuses the existing <ZoomableImage> from ProductCheckoutPage
            (cursor: zoom-in + hover scale that tracks the pointer) rather than a
            new mechanism. */}
        <div className={styles.mainImage} key={main.src} style={aspectRatio ? { aspectRatio } : undefined}>
          <ZoomableImage image={main} alt={main.alt || ''} sizes="(max-width: 991px) 100vw, 60vw" />
        </div>
        {count > 1 && (
          <button type="button" className={[styles.arrow, styles.arrowRight].join(' ')} onClick={() => go(1)} aria-label="Next image">
            <Chevron dir="right" />
          </button>
        )}
        {count > 1 && <div className={styles.counter}>{active + 1} / {count}</div>}
      </div>

      {count > 1 && (
        <div className={styles.thumbs} ref={stripRef} role="tablist" aria-label="Product images">
          {images.map((img, i) => (
            <button
              type="button"
              key={img.src}
              ref={(el) => { thumbRefs.current[i] = el }}
              className={[styles.thumb, i === active && styles.thumbActive].filter(Boolean).join(' ')}
              onClick={() => setActive(i)}
              aria-label={`View image ${i + 1}`}
              aria-selected={i === active}
              role="tab"
            >
              <OptimizedImage image={img} alt={img.alt || ''} layout="fill" objectFit="cover" sizes="120px" reserveSpace={false} />
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

function Chevron({ dir }) {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      {dir === 'left' ? <polyline points="15 18 9 12 15 6" /> : <polyline points="9 18 15 12 9 6" />}
    </svg>
  )
}

export default ProductGallery
